import { useEffect, useState } from "react";
import "./DataestatesummaryTab.scss";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  Explore as ExploreIcon,
  TrackChanges as TrackChangesIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedUserIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";
import { FETCH_DATA_ESTATE_SUMMARY_TAB } from "../DataTagConstants";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";

const HEAD_ICON_MAP = {
  DATA_OWNERSHIP: PersonIcon,
  EXPOSURE_RISK: PublicIcon,
  RISK_COMPLIANCE_OVERSIGHT: VerifiedUserIcon,
  ARCHIVAL_RETENTION_STRATEGY: HistoryIcon,
};

const HIGHLIGHT_WORDS = [
  "highly",
  "high",
  "risk",
  "medium",
  "sensitivity",
  "sensitive",
  "publicly",
  "low",
  "public",
  "PCI_DSS",
  "PII",
  "GDPR",
  "Confidential",
  "Financial",
  "PHI/HIPAA",
  "Regulated",
];

function highlightHtml(raw) {
  if (typeof raw !== "string") return "-";

  let updated = raw;
  HIGHLIGHT_WORDS.forEach((word) => {
    const regex = new RegExp(`(${word})`, "gi");
    updated = updated.replace(regex, "<b>$1</b>");
  });
  updated = updated.replace(/\n/g, "<br />");

  return DOMPurify.sanitize(updated);
}

// "DATA_OWNERSHIP" -> "Data Ownership"
function formatHead(head = "") {
  return head
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatMetricValue(value) {
  if (
    value === null ||
    value === undefined ||
    value === "null" ||
    value === ""
  ) {
    return "—";
  }
  return value;
}

export default function DataEstateSummaryTab() {
  const dispatch = useDispatch();

  const dataEstateSummaryTabLoader = useSelector(
    (state) => state.dataTagReducer?.dataEstateSummaryTabLoader,
  );
  const dataEstateSummaryTab = useSelector(
    (state) => state.dataTagReducer?.dataEstateSummaryTab,
  );

  useEffect(() => {
    dispatch({ type: FETCH_DATA_ESTATE_SUMMARY_TAB });
  }, [dispatch]);

  const narrativePoints = dataEstateSummaryTab?.narrativePoints || [];
  const hardeningItems = dataEstateSummaryTab?.hardeningItems || [];

  const [activeHead, setActiveHead] = useState(narrativePoints[0]?.head);
  const [openActions, setOpenActions] = useState({});

  const toggleAction = (head) =>
    setOpenActions((prev) => ({ ...prev, [head]: !prev[head] }));

  if (dataEstateSummaryTabLoader) {
    return null;
  }

  const activePoint =
    narrativePoints.find((point) => point.head === activeHead) ||
    narrativePoints[0];
  const ActiveIcon = activePoint
    ? HEAD_ICON_MAP[activePoint.head] || AssignmentIcon
    : AssignmentIcon;

  return (
    <>
      {dataEstateSummaryTabLoader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTag"
        ></img>
      )}
      <div className="data-estate-summary">
        <div className="des-section">
          <div className="des-section-label">
            <span className="des-section-label-bar"></span>
            <ExploreIcon
              className="des-section-label-icon"
              style={{ fontSize: 16 }}
            />
            Executive Summary
          </div>

          <div className="des-narrative-panel">
            <div className="des-narrative-tabs">
              {narrativePoints.map((point) => {
                const Icon = HEAD_ICON_MAP[point.head] || AssignmentIcon;
                const isActive = point.head === activePoint?.head;

                return (
                  <button
                    type="button"
                    key={point.head}
                    className={`des-narrative-tab ${isActive ? "active" : ""}`}
                    onClick={() => setActiveHead(point.head)}
                    aria-pressed={isActive}
                  >
                    <Icon style={{ fontSize: 15 }} />
                    {formatHead(point.head)}
                  </button>
                );
              })}
            </div>

            {activePoint && (
              <div className="des-narrative-detail" key={activePoint.head}>
                <div className="des-narrative-detail-header">
                  <span className="des-narrative-icon-wrap">
                    <ActiveIcon style={{ fontSize: 17 }} />
                  </span>
                  <span className="des-narrative-detail-title">
                    {formatHead(activePoint.head)}
                  </span>
                </div>

                <p
                  className="des-narrative-detail-text"
                  dangerouslySetInnerHTML={{
                    __html: highlightHtml(activePoint.text),
                  }}
                />

                {activePoint.subPoints?.length > 0 && (
                  <ul className="des-row-subpoints">
                    {activePoint.subPoints.map((subPoint, subIndex) => (
                      <li
                        key={subIndex}
                        dangerouslySetInnerHTML={{
                          __html: highlightHtml(subPoint),
                        }}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="des-section">
          <div className="des-section-label">
            <span className="des-section-label-bar"></span>
            <TrackChangesIcon
              className="des-section-label-icon"
              style={{ fontSize: 16 }}
            />
            Recommended Actions
          </div>

          <div className="des-action-list">
            {hardeningItems.map((item) => {
              const Icon = HEAD_ICON_MAP[item.head] || AssignmentIcon;
              const severityClass = `sev-${(item.severity || "medium").toLowerCase()}`;
              const metrics = item.metrics || [];
              const isOpen = !!openActions[item.head];

              return (
                <div className="des-action-row" key={item.head}>
                  <button
                    type="button"
                    className="des-row-btn"
                    onClick={() => toggleAction(item.head)}
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`des-action-stripe ${severityClass}`}
                      aria-hidden="true"
                    ></span>
                    <span className={`des-action-icon-wrap ${severityClass}`}>
                      <Icon style={{ fontSize: 18 }} />
                    </span>
                    <span className="des-row-content">
                      <span className="des-action-row-header">
                        <span className="des-row-title">
                          {formatHead(item.head)}
                        </span>
                        <span className={`des-rec-sev-badge ${severityClass}`}>
                          {(item.severity || "MEDIUM").toUpperCase()}
                        </span>
                      </span>
                      <p className="des-row-trunc">{item.text}</p>
                      {metrics.length > 0 && (
                        <span className="des-action-chips">
                          {metrics.map((m) => (
                            <span className="des-action-chip" key={m.label}>
                              <strong>{formatMetricValue(m.value)}</strong>
                              &nbsp;{m.label}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                    <ExpandMoreIcon
                      className="des-row-chevron"
                      style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>

                  <div className={`des-row-collapse ${isOpen ? "open" : ""}`}>
                    <div className="des-row-collapse-inner">
                      <div className="des-row-expanded des-action-row-expanded">
                        <p className="des-row-full-text">{item.text}</p>
                        {item.subItems?.length > 0 && (
                          <div className="des-rec-action-callout">
                            <div className="des-rec-action-label">
                              <ArrowForwardIcon style={{ fontSize: 12 }} />
                              Recommended Action
                              {item.subItems.length > 1 ? "s" : ""}
                            </div>
                            <ul className="des-rec-action-list">
                              {item.subItems.map((step, stepIndex) => (
                                <li key={stepIndex}>{step}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
